import { useState } from "react";
import { ChevronDown, Plus, MessageSquare } from "lucide-react";

interface Conversation {
  id: number;
  title: string | null;
  last_message_at: string | null;
  created_at: string;
}

interface AiConversationSelectProps {
  conversations: Conversation[];
  activeConversationId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "gerade eben";
  if (mins < 60) return `vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "gestern";
  if (days < 7) return `vor ${days} Tagen`;
  return new Date(dateStr).toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
  });
}

export const AiConversationSelect: React.FC<AiConversationSelectProps> = ({
  conversations,
  activeConversationId,
  onSelect,
  onNew,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const activeConversation = conversations.find((c) => c.id === activeConversationId);
  const label = activeConversation?.title || "Neuer Chat";

  return (
    <div className="flex items-center gap-1 px-3 py-1.5 border-b border-gray-100">
      <div className="relative flex-1 min-w-0">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 w-full text-left px-2 py-1 rounded hover:bg-gray-50 transition-colors"
          aria-label="Unterhaltung wechseln"
          data-testid="ai-conversation-select"
        >
          <MessageSquare className="h-3 w-3 text-gray-400 shrink-0" />
          <span className="text-xs text-gray-700 truncate">{label}</span>
          <ChevronDown className="h-3 w-3 text-gray-400 shrink-0 ml-auto" />
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute left-0 right-0 top-full z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400">
                  Keine Unterhaltungen
                </div>
              ) : (
                conversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => {
                      onSelect(conv.id);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors ${
                      conv.id === activeConversationId ? "bg-gray-50" : ""
                    }`}
                    data-testid={`ai-conversation-item-${conv.id}`}
                  >
                    <div className="text-xs text-gray-700 truncate">
                      {conv.title || "Unterhaltung"}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {formatRelativeTime(conv.last_message_at || conv.created_at)}
                    </div>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>

      <button
        onClick={onNew}
        className="shrink-0 p-1 rounded hover:bg-gray-100 transition-colors"
        aria-label="Neuen Chat starten"
        data-testid="ai-new-chat-button"
      >
        <Plus className="h-3.5 w-3.5 text-gray-500" />
      </button>
    </div>
  );
};
